import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Checkbox, DatePicker, Form, Input, Select} from 'antd';

import './index.less'
import './mobile.less'

import {formRulesPhoneNumValidator, getUsingClassName} from '@/common';

const {Option} = Select;

const defaultProps = {
  className: '',
  emailRequired: true,
  isShowBaseInfo: true,
  isShowCompanyInfo: true,
  isShowAddressInfo: true,
  isShowPassword: false,
  isRequiredBusinessType: false,
  isShowEmail: true,
  isChangePassword: false,
  isAccountApplication: false,
  onFormFinish: () => {
  },
  onFormFinishFailed: () => {
  },
  initialValues: undefined,
  disabledInputNameList: [],
};

const UserInfoForm = forwardRef(function (props, ref) {
  const options = Object.assign({}, defaultProps, props);

  const {t} = useTranslation()

  const commonState = useSelector(state => state.common)
  const rootClassName = commonState.isMobile ? 'user-info-form-mobile-container' : 'user-info-form-container'

  const {disabledInputNameList} = options
  
  const formRef = useRef(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [newPasswordEdit, setNewPasswordEdit] = useState(false);

  const businessTypeList = [
    {
      label: 'Corporation',
      value: 'Corporation',
    },
    {
      label: 'Partnership',
      value: 'Partnership',
    },
    {
      label: 'Sole Ownership',
      value: 'Sole Ownership',
    },
  ]

  const submit = () => formRef.current?.submit()
  const resetFields = () => formRef.current?.resetFields()

  const className = getUsingClassName(
    [rootClassName],
    [options.className]
  )
  
  useImperativeHandle(ref, () => ({
    submit,
    resetFields,
  }))

  const renderSectionTitle = (num, title) => (
    <div className={'user-info-form-section-title'}>
      <span>{num}</span>
      <div>{t(title)}</div>
    </div>
  )

  const renderAccountApplicationForm = () => (
    <>
      {renderSectionTitle(1, 'Primary Information')}
      <Form.Item
        label={t('Name')}
        name="name"
        rules={[
          {
            required: true,
            message: t('Please enter your first name and last name'),
          },
        ]}>
        <Input disabled={disabledInputNameList.includes('company')} placeholder={t('Enter your first name and last name')} />
      </Form.Item>
      <div className={'user-info-form-row'}>
        {options.isShowEmail && <Form.Item
          label={t('Email Address')}
          name="emailAddress"
          rules={[
            {
              required: options.emailRequired,
              message: t('Please enter your email address'),
            },
            {
              type: 'email',
              message: t('The enter is not valid Email Address'),
            }
          ]}>
          <Input
            disabled={(disabledInputNameList.includes('emailAddress') || !options.emailRequired)}
            placeholder={t('Enter your email address')}
          />
        </Form.Item>}
        <Form.Item
          label={t('Phone number')}
          name="phoneNumber"
          rules={[
            {
              required: true,
              message: t('Please enter your phone number'),
            },
            {
              validator: (_, value) => formRulesPhoneNumValidator(_, value, t('The enter is not valid Phone number')),
            }
          ]}>
          <Input disabled={disabledInputNameList.includes('phoneNumber')} placeholder={t('Enter your phone number')} />
        </Form.Item>
      </div>
      <div className={'user-info-form-row'}>
        <Form.Item
          label={t('Company Name')}
          name="company"
          rules={[
            {
              required: true,
              message: t('Please enter your company name'),
            },
          ]}>
          <Input disabled={disabledInputNameList.includes('company')} placeholder={t('Enter your company name')} />
        </Form.Item>
        <Form.Item label={t('Fax')} name="fax">
          <Input placeholder={t('Fax')} />
        </Form.Item>
      </div>
      <Form.Item
        label={t('Company Address')}
        name="address"
        rules={[
          {
            required: true,
            message: t('Please enter your address'),
          },
        ]}>
        <Input disabled={disabledInputNameList.includes('address')} placeholder={t('Enter your address')} />
      </Form.Item>
      <div className={'user-info-form-row user-info-form-row-3'}>
        <Form.Item
          label={t('City')}
          name="city"
          rules={[
            {
              required: true,
              message: t('Please enter your address city'),
            },
          ]}>
          <Input disabled={disabledInputNameList.includes('city')} placeholder={t('City')} />
        </Form.Item>
        <Form.Item
          label={t('State')}
          name="state"
          rules={[
            {
              required: true,
              message: t('Please enter your address state'),
            },
          ]}>
          <Input disabled={disabledInputNameList.includes('state')} placeholder={t('State')} />
        </Form.Item>
        <Form.Item
          label={t('Zip Code')}
          name="zipCode"
          rules={[
            {
              required: true,
              message: t('Please enter your address zipcode'),
            },
          ]}>
          <Input disabled={disabledInputNameList.includes('zipCode')} placeholder={t('Zip Code')} />
        </Form.Item>
      </div>
      <Form.Item
        label={t('Business Types')}
        name="businessType"
        rules={[
          {
            required: options.isRequiredBusinessType,
            message: t('Please select company type'),
          },
        ]}>
        <Checkbox.Group className={'user-info-form-checkbox-group'}>
          <Checkbox value={'Corporation'}>{t('Corporation')}</Checkbox>
          <Checkbox value={'Partnership'}>{t('Partnership')}</Checkbox>
          <Checkbox value={'Sole Ownership'}>{t('Sole Ownership')}</Checkbox>
        </Checkbox.Group>
      </Form.Item>
      <div className={'user-info-form-row'}>
        <Form.Item label={t('Date Business Established')} name="business_established_date" rules={[{required: true, message: t('Please enter date business established')}]}>
          <DatePicker placeholder={'MM/DD/YYYY'} />
        </Form.Item>
        <Form.Item label={t('Number of Employees')} name="num_employees" rules={[{required: true, message: t('Please enter num of employees')}]}>
          <Input placeholder={t('Number of Employees')} />
        </Form.Item>
      </div>
      <div className={'user-info-form-row user-info-form-row-3'}>
        <Form.Item label={'Federal Tax ID'} name="einNumber" rules={[{required: true, message: t('Please enter federal tax id (EIN)')}]}>
          <Input disabled={disabledInputNameList.includes('einNumber')} placeholder={t('Enter your company EIN number')} />
        </Form.Item>
        <Form.Item label={'State Tax ID'} name="state_tax_id" rules={[{required: true, message: t('Please enter state tax if')}]}>
          <Input placeholder={'State Tax ID'} />
        </Form.Item>
        <Form.Item label={'Resale #'} name="resaleNumber" rules={[{required: true, message: t('Please enter resale #')}]}>
          <Input placeholder={'Resale #'} />
        </Form.Item>
      </div>

      {renderSectionTitle(2, 'Principals')}
      <div className={'user-info-form-row user-info-form-row-3'}>
        <Form.Item label={t('President')} name="president">
          <Input placeholder={t('President')} />
        </Form.Item>
        <Form.Item label={t('Purchasing Manager')} name="purchasing_manager">
          <Input placeholder={t('Purchasing Manager')} />
        </Form.Item>
        <Form.Item label={'A/P Manager'} name="ap_manager">
          <Input placeholder={'A/P Manager'} />
        </Form.Item>
      </div>

      {renderSectionTitle(3, 'Bank Reference')}
      <div className={'user-info-form-row'}>
        <Form.Item label={t('Bank Name')} name="bank_name">
          <Input placeholder={t('Bank Name')} />
        </Form.Item>
        <Form.Item label={t('Branch')} name="bank_branch">
          <Input placeholder={t('Branch')} />
        </Form.Item>
      </div>
      <Form.Item label={t('Bank Address')} name="bank_address">
        <Input placeholder={t('Bank Address')} />
      </Form.Item>
      <div className={'user-info-form-row user-info-form-row-3'}>
        <Form.Item label={'Bank Acct #'} name="bank_account_no">
          <Input placeholder={'Bank Acct #'} />
        </Form.Item>
        <Form.Item label={t('Tel')} name="bank_tel">
          <Input placeholder={t('Tel')} />
        </Form.Item>
        <Form.Item label={t('Contact Person')} name="bank_contact_person">
          <Input placeholder={t('Contact Person')} />
        </Form.Item>
      </div>

      {renderSectionTitle(4, 'Trade References')}
      {[1, 2, 3].map(item => (
        <div className={'user-info-form-reference'} key={`trade_reference_${item}`}>
          <div className={'user-info-form-reference-title'}>{t('Reference')} {item}</div>
          <div className={'user-info-form-row user-info-form-row-3'}>
            <Form.Item label={t('Company Name')} name={`trade_ref_${item}_company_name`}>
              <Input placeholder={t('Company Name')} />
            </Form.Item>
            <Form.Item label={t('Tel')} name={`trade_ref_${item}_tel`}>
              <Input placeholder={t('Tel')} />
            </Form.Item>
            <Form.Item label={t('Fax')} name={`trade_ref_${item}_fax`}>
              <Input placeholder={t('Fax')} />
            </Form.Item>
          </div>
          <Form.Item label={t('Address')} name={`trade_ref_${item}_address`}>
            <Input placeholder={t('Address')} />
          </Form.Item>
        </div>
      ))}
    </>
  )

  // here is edit
  return (
    <Form
      className={className}
      ref={formRef}
      layout={'vertical'}
      requiredMark={false}
      onFinish={options.onFormFinish}
      onFinishFailed={options.onFormFinishFailed}
      scrollToFirstError={true}
      autoComplete="off"
      initialValues={options.initialValues}>
      {options.isAccountApplication && renderAccountApplicationForm()}
      {
        !options.isAccountApplication &&
        <>
      {
        options.isShowBaseInfo &&
        <>
          <Form.Item
            label={t('Name')}
            name="name"
            rules={[
              {
                required: true,
                message: t('Please enter your name'),
              },
            ]}>
            <Input
              disabled={disabledInputNameList.includes('name')}
              placeholder={t('Enter your name')}
            />
          </Form.Item>
          {options.isShowEmail && <Form.Item
            label={t('Email Address')}
            name="emailAddress"
            rules={[
              {
                required: options.emailRequired,
                message: t('Please enter your email address'),
              },
              {
                type: 'email',
                message: t('The enter is not valid Email Address'),
              }
            ]}>
            <Input
              disabled={(disabledInputNameList.includes('emailAddress') || !options.emailRequired)}
              placeholder={t('Enter your email address')}
            />
          </Form.Item>}
          <Form.Item
            label={t('Phone number')}
            name="phoneNumber"
            rules={[
              {
                required: true,
                message: t('Please enter your phone number'),
              },
              {
                validator: (_, value) => formRulesPhoneNumValidator(_, value, t('The enter is not valid Phone number')),
              }
            ]}>
            <Input
              disabled={disabledInputNameList.includes('phoneNumber')}
              placeholder={t('Enter your phone number')}
            />
          </Form.Item>
        </>
      }
      {
        options.isShowCompanyInfo &&
        <>
          <Form.Item
            label={t('Company')}
            name="company"
            rules={[
              {
                required: true,
                message: t('Please enter your company name'),
              },
            ]}>
            <Input
              disabled={disabledInputNameList.includes('company')}
              placeholder={t('Enter your company name')}
            />
          </Form.Item>
          <Form.Item
            label={t('Company Phone')}
            name="companyPhone"
            rules={[
              {
                validator: (_, value) => formRulesPhoneNumValidator(_, value, t('The enter is not valid Company Phone')),
              }
            ]}>
            <Input
              disabled={disabledInputNameList.includes('companyPhone')}
              placeholder={t('Enter your company phone')}
            />
          </Form.Item>
          {options.isShowEmail && <Form.Item
            label={t('Email Address')}
            name="emailAddress"
            rules={[
              {
                required: options.emailRequired,
                message: t('Please enter your email address'),
              },
              {
                type: 'email',
                message: t('The enter is not valid Email Address'),
              }
            ]}>
            <Input
              disabled={(disabledInputNameList.includes('emailAddress') || !options.emailRequired)}
              placeholder={t('Enter your email address')}
            />
          </Form.Item>}
          <Form.Item
            label={t('Business Type')}
            name="businessType"
            id={'user-info-form-monthly-volume-root'}
            rules={[
              {
                required: options.isRequiredBusinessType,
                message: t('Please select company type'),
              },
            ]}>
            <Select
              disabled={disabledInputNameList.includes('businessType')}
              placeholder={t('Enter your company business type')}
              getPopupContainer={() => document.getElementById('user-info-form-monthly-volume-root')}>
              {
                businessTypeList.map((item, index) => {
                  return <Option
                    value={item.value}
                    key={'business_type_' + index}>
                    {t(item.label)}
                  </Option>
                })
              }
            </Select>
          </Form.Item>
          <Form.Item
            label={'EIN Number'}
            name="einNumber">
            <Input
              disabled={disabledInputNameList.includes('einNumber')}
              placeholder={t('Enter your company EIN number')}
            />
          </Form.Item>
        </>
      }
      {
        options.isShowAddressInfo &&
        <>
          <Form.Item
            label={t('Address')}
            name="address"
            rules={[
              {
                required: true,
                message: t('Please enter your address'),
              },
            ]}>
            <Input
              disabled={disabledInputNameList.includes('address')}
              placeholder={t('Enter your address')}
            />
          </Form.Item>
          <Form.Item
            label={t('City')}
            name="city"
            rules={[
              {
                required: true,
                message: t('Please enter your address city'),
              },
            ]}>
            <Input
              disabled={disabledInputNameList.includes('city')}
              placeholder={t('Enter your address city')}
            />
          </Form.Item>
          <Form.Item
            label={t('State')}
            name="state"
            rules={[
              {
                required: true,
                message: t('Please enter your address state'),
              },
            ]}>
            <Input
              disabled={disabledInputNameList.includes('state')}
              placeholder={t('Enter your address state')}
            />
          </Form.Item>
          <Form.Item
            label={t('Zip Code')}
            name="zipCode"
            rules={[
              {
                required: true,
                message: t('Please enter your address zipcode'),
              },
            ]}>
            <Input
              disabled={disabledInputNameList.includes('zipCode')}
              placeholder={t('Enter your address zipcode')}
            />
          </Form.Item>
        </>
      }
      {
        options.isShowPassword &&
        <>
          <div className={'user-info-form-password-container'}>
            <Form.Item
              label={t('Password')}
              name="password"
              rules={[
                {
                  required: options.isChangePassword ? newPasswordEdit : true,
                  message: t('Please enter your password'),
                },
              ]}>
              <Input.Password
                disabled={options.isChangePassword && !newPasswordEdit || (!options.isChangePassword && newPasswordEdit)}
                placeholder={options.isChangePassword && !newPasswordEdit?'******':t('Enter your password')}
                visibilityToggle={{
                  visible: passwordVisible,
                  onVisibleChange: setPasswordVisible,
                }}
                autoComplete={'new-password'}
                iconRender={() => null}
              />
            </Form.Item>
            <div>
            {options.isChangePassword ? <div className={newPasswordEdit?'user-info-form-show-hide-new-password':'user-info-form-show-hide-new-password2'}
              onClick={() => setNewPasswordEdit(prevState=> !prevState)}>
              {t('Change Password')}
            </div> : <></>}
            {(!options.isChangePassword || options.isChangePassword && newPasswordEdit) ? <div
              className={'user-info-form-show-hide-password'}
              onClick={() => setPasswordVisible(prevState=> !prevState)}>
              {passwordVisible ? t('Hide') : t('Show')} {t('Password')}
            </div> : <></> }
            </div>
            {newPasswordEdit && 
            <>
            <Form.Item
              label={t('New Password')}
              name="new_password"
              rules={[
                {
                  required: newPasswordEdit,
                  message: t('Please enter your new password'),
                },
              ]}>
              <Input.Password
                placeholder={t('Enter your new password')}
                visibilityToggle={{
                  visible: passwordVisible,
                  onVisibleChange: setPasswordVisible,
                }}
                autoComplete={'new-password'}
                iconRender={() => null}
              />
            </Form.Item>
            </>
            }
          </div>
        </>
      }
        </>
      }
    </Form>
  )
})

export default UserInfoForm
